import * as _ from 'lodash';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { Address } from '../../types/address';
import { TextModel } from '../primitive/text.model';
import { CompositeModel } from './composite.model';
import { CseDopModel } from './cse-dop.model';
import { TimesheetAddressModel } from './timesheet-address.model';
import { DropdownModel } from './dropdown.model';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import { ConfigService } from '../../services/config/config.service';

const addrKeyMultiplier = 10000000;

export class AddressModel extends CompositeModel {
    private clientManager: ClientManagerInterface;

    public name1: TextModel;
    public name2: TextModel;
    public name3: TextModel;
    public name4: TextModel;
    // P58 has name long fields with 40 chars, but for material request and return name1 and name2 of 30 characters are needed
    public name1shortP58: string;
    public name2shortP58: string;
    public street: TextModel;
    public city: TextModel;
    public statesOfUSA: DropdownModel<TextModel>;
    public postCode: TextModel;
    public country: TextModel;
    public building: TextModel;
    public room: TextModel;
    public floor: TextModel;
    public department: TextModel;
    public contactName1: TextModel;
    public contactName2: TextModel;
    public contactPhone: TextModel;
    public contactType: TextModel;
    public email: TextModel;
    public phone: TextModel;
    public fax1: TextModel;
    public fax2: TextModel;
    public fax3: TextModel;
    public fax4: TextModel;
    public addrText: TextModel;
    public addrNumber: TextModel;
    public addrKey: number;
    public state: TextModel;
    public visible: boolean;
    public allowToHide: boolean;
    public isFromAddressSet: boolean;
    public constructor(
        name1: string,
        name2: string,
        name3: string,
        name4: string,
        street: string,
        city: string,
        postCode: string,
        country: string,
        building: string,
        room: string,
        floor: string,
        department: string,
        contactName1: string,
        contactName2: string,
        contactPhone: string,
        contactType: string,
        email: string,
        phone: string,
        fax1: string,
        fax2: string,
        fax3: string,
        fax4: string,
        addrText: string,
        addrNumber: string,
        state: string,
        name1shortP58: string,
        name2shortP58: string,
        visible = true,
        allowToHide = false,
        isFromAddressSet = false,
    ) {
        super('AddressModel');

        const injector = CustomInjector.getInjector();
        this.clientManager = injector.get(ClientManagerInstanceToken);

        this.name1 = <TextModel>this.set(new TextModel('Name1', name1));
        this.name2 = <TextModel>this.set(new TextModel('Name2', name2));
        this.name3 = <TextModel>this.set(new TextModel('Name3', name3));
        this.name4 = <TextModel>this.set(new TextModel('Name4', name4));
        this.name1shortP58 = name1shortP58;
        this.name2shortP58 = name2shortP58;
        this.street = <TextModel>this.set(new TextModel('Street', street));
        this.city = <TextModel>this.set(new TextModel('City', city));
        this.postCode = <TextModel>this.set(new TextModel('PostCode', postCode));
        this.country = <TextModel>this.set(new TextModel('Country', country));
        this.building = <TextModel>this.set(new TextModel('Building', building));
        this.room = <TextModel>this.set(new TextModel('Room', room));
        this.floor = <TextModel>this.set(new TextModel('Floor', floor));
        this.department = <TextModel>this.set(new TextModel('Department', department));
        this.contactName1 = <TextModel>this.set(new TextModel('ContactName1', contactName1));
        this.contactName2 = <TextModel>this.set(new TextModel('ContactName2', contactName2));
        this.contactPhone = <TextModel>this.set(new TextModel('ContactPhone', contactPhone));
        this.contactType = <TextModel>this.set(new TextModel('ContactType', contactType));
        this.email = <TextModel>this.set(new TextModel('TelNumber', email));
        this.phone = <TextModel>this.set(new TextModel('Phone', phone));
        this.fax1 = <TextModel>this.set(new TextModel('FaxNumber1', fax1));
        this.fax2 = <TextModel>this.set(new TextModel('FaxNumber2', fax2));
        if (this.clientManager.isNgmAA()) {
            this.fax3 = new TextModel('FaxNumber3', fax3);
            this.fax4 = new TextModel('FaxNumber4', fax4);
        }
        this.addrText = <TextModel>this.set(new TextModel('AddrNotes', addrText));
        this.addrNumber = <TextModel>this.set(new TextModel('AddrNumber', addrNumber));
        this.addrKey = Math.floor(Math.random() * addrKeyMultiplier);
        if (this.clientManager.isPaceUS()) {
            this.state = new TextModel('State', state);
        }
        this.visible = visible;
        this.allowToHide = allowToHide;
        this.isFromAddressSet = isFromAddressSet;
    }

    public getTimesheetAddress(): TimesheetAddressModel {
        return new TimesheetAddressModel(
            this.street.value,
            this.city.value,
            this.postCode.value,
            this.country.value,
            this.addrText.value,
            this.addrNumber.value,
            this.statesOfUSA?.selectedEntry?.model.value,
        );
    }

    public static fromTimesheetAddress(timesheetAddress: TimesheetAddressModel): AddressModel {
        const address = AddressModel.build({
            street: timesheetAddress.street.value,
            city: timesheetAddress.city.value,
            postCode: timesheetAddress.postCode.value,
            country: timesheetAddress.country.value,
            addrText: timesheetAddress.addrText.value,
            addrNumber: timesheetAddress.addrNumber.value,
        });
        if (typeof timesheetAddress.statesOfUSA !== 'undefined') {
            address.statesOfUSA = timesheetAddress.statesOfUSA;
        }
        return address;
    }

    /**
     * This logic applies only for the USA region.
     *
     * If the customer address is selected, the `Notification.city` field contains a value in the format `<City;State>`.
     * We extract the state part and select it from the state dropdown.
     * If the state is not available in the dropdown, no selection is made.
     *
     * If DOP is selected, the state dropdown value comes from the backend via `CseDopSet`,
     * and we bind the dropdown to that backend-provided state value.
     */
    public static updateStateOfUSA(
        address: AddressModel | CseDopModel | TimesheetAddressModel,
        selectedState = '',
        isDopAddress = false,
    ): void {
        const injector = CustomInjector.getInjector();
        const valueHelperService = injector.get(ValueHelperService);
        const configService = injector.get(ConfigService);

        if (!configService.getEnableStatesOfUSADropdown()) {
            return;
        }

        if (typeof address.statesOfUSA === 'undefined') {
            address.statesOfUSA = new DropdownModel<TextModel>('States', []);
            address.statesOfUSA.value = valueHelperService
                .getStatesOfUSA()
                .map((vh) => new DisplayDecorator(vh.descText, new TextModel('State', vh.code)));
        }

        if (selectedState) {
            address.statesOfUSA.selectByPredicate((addr) => addr.model.value === selectedState);
        }

        let extractedCity = '';
        let extractedStateCode = '';

        if (address instanceof TimesheetAddressModel) {
            [extractedCity, extractedStateCode] = address.city.value.split(';');
        } else if (isDopAddress) {
            address.statesOfUSA.deselect();
            extractedCity = address.city.value;
            extractedStateCode = address.state.value;
        } else {
            [extractedCity, extractedStateCode] = address.city.value.split(';');
        }

        address.statesOfUSA.selectByValue(extractedStateCode);
        address.city.value = extractedCity;
    }

    public static fromCseDopModel(cseDopModel: CseDopModel): AddressModel {
        return new AddressModel(
            cseDopModel.name1Long.length > 0 ? cseDopModel.name1Long : cseDopModel.name1.value,
            cseDopModel.name2Long.length > 0 ? cseDopModel.name2Long : cseDopModel.name2.value,
            '',
            '',
            cseDopModel.street.value,
            cseDopModel.city.value,
            cseDopModel.postalCode.value,
            '',
            cseDopModel.building.value,
            cseDopModel.room.value,
            cseDopModel.floor.value,
            cseDopModel.department.value,
            cseDopModel.contactPerson.value,
            '',
            '',
            '',
            '',
            cseDopModel.phone.value,
            '',
            '',
            '',
            '',
            '',
            cseDopModel.addrNumber.value,
            '',
            cseDopModel.name1.value,
            cseDopModel.name2.value,
        );
    }

    public getAddressState(): Address {
        return {
            addrKey: this.addrKey,
            addrNumber: this.addrNumber.value,
            addrText: this.addrText.value,
            building: this.building.value,
            city: this.city.value,
            contactName1: this.contactName1.value,
            contactName2: this.contactName2.value,
            contactPhone: this.contactPhone.value,
            contactType: this.contactType.value,
            country: this.country.value,
            department: this.department.value,
            fax1: this.fax1.value,
            fax2: this.fax2.value,
            fax3: this.fax3 ? this.fax3.value : '',
            fax4: this.fax4 ? this.fax4.value : '',
            floor: this.floor.value,
            name1: this.name1.value,
            name2: this.name2.value,
            name3: this.name3.value,
            name4: this.name4.value,
            email: this.email.value,
            phone: this.phone.value,
            postCode: this.postCode.value,
            room: this.room.value,
            state: this.state ? this.state.value : '',
            street: this.street.value,
        };
    }

    public get completeName(): string {
        return (this.name1.value + ' ' + this.name2.value + ' ' + this.name3.value + ' ' + this.name4.value).trim();
    }

    public static compare(firstModel: AddressModel, secondModel: AddressModel): boolean {
        return _.isEqual(firstModel, secondModel);
    }

    /**
     * Create an AddressModel without having to supply all the parameters at once.
     * Can also be used to re-create an address model from data stored in localStorage.
     *
     * @param root0
     * @param root0.name1
     * @param root0.name2
     * @param root0.name3
     * @param root0.name4
     * @param root0.street
     * @param root0.city
     * @param root0.postCode
     * @param root0.country
     * @param root0.building
     * @param root0.room
     * @param root0.floor
     * @param root0.department
     * @param root0.contactName1
     * @param root0.contactName2
     * @param root0.contactPhone
     * @param root0.contactType
     * @param root0.email
     * @param root0.phone
     * @param root0.fax1
     * @param root0.fax2
     * @param root0.fax3
     * @param root0.fax4
     * @param root0.addrText
     * @param root0.addrNumber
     * @param root0.state
     * @param root0.name1shortP58
     * @param root0.name2shortP58
     * @param root0.visible
     * @param root0.allowToHide
     * @param root0.isFromAddressSet
     */
    public static build({
        name1 = '',
        name2 = '',
        name3 = '',
        name4 = '',
        street = '',
        city = '',
        postCode = '',
        country = '',
        building = '',
        room = '',
        floor = '',
        department = '',
        contactName1 = '',
        contactName2 = '',
        contactPhone = '',
        contactType = '',
        email = '',
        phone = '',
        fax1 = '',
        fax2 = '',
        fax3 = '',
        fax4 = '',
        addrText = '',
        addrNumber = '',
        state = '',
        name1shortP58 = '',
        name2shortP58 = '',
        visible = true,
        allowToHide = false,
        isFromAddressSet = false,
    }: {
        name1?: string;
        name2?: string;
        name3?: string;
        name4?: string;
        street?: string;
        city?: string;
        postCode?: string;
        country?: string;
        building?: string;
        room?: string;
        floor?: string;
        department?: string;
        contactName1?: string;
        contactName2?: string;
        contactPhone?: string;
        contactType?: string;
        email?: string;
        phone?: string;
        fax1?: string;
        fax2?: string;
        fax3?: string;
        fax4?: string;
        addrText?: string;
        addrNumber?: string;
        state?: string;
        name1shortP58?: string;
        name2shortP58?: string;
        visible?: boolean;
        allowToHide?: boolean;
        isFromAddressSet?: boolean;
    }): AddressModel {
        return new AddressModel(
            name1,
            name2,
            name3,
            name4,
            street,
            city,
            postCode,
            country,
            building,
            room,
            floor,
            department,
            contactName1,
            contactName2,
            contactPhone,
            contactType,
            email,
            phone,
            fax1,
            fax2,
            fax3,
            fax4,
            addrText,
            addrNumber,
            state,
            name1shortP58,
            name2shortP58,
            visible,
            allowToHide,
            isFromAddressSet,
        );
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     *
     * @returns {JsonAddressModel}
     */
    public toJSON(): JsonAddressModel {
        return {
            name1: this.name1.value,
            name2: this.name2.value,
            name3: this.name3.value,
            name4: this.name4.value,
            name1shortP58: this.name1shortP58,
            name2shortP58: this.name2shortP58,
            street: this.street.value,
            city: this.city.value,
            postCode: this.postCode.value,
            country: this.country.value,
            building: this.building.value,
            room: this.room.value,
            floor: this.floor.value,
            department: this.department.value,
            contactName1: this.contactName1.value,
            contactName2: this.contactName2.value,
            contactPhone: this.contactPhone.value,
            contactType: this.contactType.value,
            email: this.email.value,
            phone: this.phone.value,
            fax1: this.fax1.value,
            fax2: this.fax2.value,
            fax3: this.fax3 ? this.fax3.value : '',
            fax4: this.fax4 ? this.fax4.value : '',
            addrText: this.addrText.value,
            addrNumber: this.addrNumber.value,
            state: this.state ? this.state.value : '',
            visible: this.visible,
            isFromAddressSet: this.isFromAddressSet,
            stateOfUSA: this.statesOfUSA?.selectedEntry?.model.value,
        };
    }

    public static fromOData(data: ODataAddressModel): AddressModel {
        let city = data.City;
        let state = '';
        const injector = CustomInjector.getInjector();
        const clientManager = injector.get(ClientManagerInstanceToken);
        if (clientManager.isPaceUS()) {
            // For PACE US the inside the city value we are receiving the state and the city name
            // The state and the city name are separated by ":"
            // As an output the following format is needed
            const cityAndState = city.split(':');
            state = cityAndState[0];
            city = cityAndState[1];
        }

        let name1 = data.Name1;
        let name2 = data.Name2;
        let name3 = data.Name3;
        let name4 = data.Name4;
        if (clientManager.isNgmP58()) {
            name1 = data.Name1Long;
            name2 = data.Name2Long;
            name3 = data.Name3Long;
            name4 = data.Name4Long;
        }

        return AddressModel.build({
            name1: name1,
            name2: name2,
            name3: name3,
            name4: name4,
            department: data.Department,
            contactName1: data.ContactName1,
            contactName2: data.ContactName1,
            contactPhone: data.ContactTel,
            contactType: data.ContactType,
            room: data.Room,
            building: data.Building,
            floor: data.Floor,
            street: data.Street,
            city: city,
            postCode: data.PostCode,
            country: data.Country ? data.Country : '', // TODO: remove once country is populated from backend
            phone: data.TelNumber,
            fax1: data.FaxNumber1,
            fax2: data.FaxNumber2,
            fax3: data.FaxNumber3 ? data.FaxNumber3 : '',
            fax4: data.FaxNumber4 ? data.FaxNumber4 : '',
            addrNumber: data.AddrNumber,
            state: state,
            name1shortP58: data.Name1,
            name2shortP58: data.Name2,
        });
    }

    public static fromODataShort(data: ODataShortAddressModel): AddressModel {
        return AddressModel.build({
            addrNumber: data.AddrNumber,
            street: data.Street,
            postCode: data.PostalCode,
            city: data.City,
            country: data.Country ? data.Country : '', // TODO: remove once country is populated from backend
            addrText: data.AddrNotes,
            allowToHide: true,
            isFromAddressSet: true,
        });
    }
}

export class AddressModelFromJson extends AddressModel {
    public constructor(data: JsonAddressModel) {
        super(
            data.name1,
            data.name2,
            data.name3,
            data.name4,
            data.street,
            data.city,
            data.postCode,
            data.country,
            data.building,
            data.room,
            data.floor,
            data.department,
            data.contactName1,
            data.contactName2,
            data.contactPhone,
            data.contactType,
            data.email,
            data.phone,
            data.fax1,
            data.fax2,
            data.fax3,
            data.fax4,
            data.addrText,
            data.addrNumber,
            data.state,
            data.name1shortP58,
            data.name2shortP58,
            data.visible,
            false,
            data.isFromAddressSet,
        );
        if (data.stateOfUSA) {
            AddressModel.updateStateOfUSA(this, data.stateOfUSA);
        }
    }
}

export interface ODataShortAddressModel extends ODataObject {
    AddrNumber: string;
    SearchTerm: string;
    Street: string;
    PostalCode: string;
    City: string;
    Country: string;
    AddrNotes: string;
    Counter: number;
}

export interface ODataAddressModel extends ODataObject {
    Name1: string;
    Name2: string;
    Name3: string;
    Name4: string;
    Street: string;
    City: string;
    PostCode: string;
    Country: string;
    Building: string;
    Room: string;
    Floor: string;
    Department: string;
    ContactName1: string;
    ContactName2: string;
    ContactTel: string;
    ContactType: string;
    TelNumber: string; // contains email address
    FaxNumber1: string;
    FaxNumber2: string;
    FaxNumber3: string;
    FaxNumber4: string;
    AddrNumber: string;
    NotificationNo: string;
    TaskNumber: string;
    Name1Long: string;
    Name2Long: string;
    Name3Long: string;
    Name4Long: string;
}

export interface JsonAddressModel {
    name1: string;
    name2: string;
    name3: string;
    name4: string;
    name1shortP58: string;
    name2shortP58: string;
    street: string;
    city: string;
    postCode: string;
    country: string;
    building: string;
    room: string;
    floor: string;
    department: string;
    contactName1: string;
    contactName2: string;
    contactPhone: string;
    contactType: string;
    email: string;
    phone: string;
    fax1: string;
    fax2: string;
    fax3: string;
    fax4: string;
    addrText: string;
    addrNumber: string;
    state: string;
    visible: boolean;
    isFromAddressSet: boolean;
    stateOfUSA: string | undefined;
}
